// TEN17 (docs/build-briefs/TEN17-brand-settings-and-theme.md; DEC-117, DEC-338, DEC-342 Q5, P0.22): a franchisor
// network's brand theme. PURE: no I/O, no clock, no randomness, no Node-only import, because the franchisor page
// and the app import this same file to draw the live preview and to apply the tokens, and the server imports
// it to derive the same tokens (test/ui-bundle-browser-safe.test.ts holds the browser side).
//
// A franchisor makes four choices (a font, and the background, accent and font colors; TEN17-F5, DEC-429). Everything
// else is DERIVED, deterministically, so the stored row never holds a palette that could go stale. The derived set
// contains ONLY `--color-*` and `--font-family-*` custom properties: a theme changes fonts and colors, never layout,
// sizes or spacing (UX82, UX83, UX90; the PRD theming paragraph), and the fixed semantic state colors (success,
// warning, danger, info, scrim in design/tokens.css) are never set.
//
// The derived set aims for the same legibility the shipped themes reach (test/ux83-snipsip-themes.test.ts): derived
// colors are clamped toward 4.5:1 text and 3:1 outlines where the picked colors allow it. Nothing is refused for
// contrast, lightness or likeness to a state color: the franchisor may pick any valid #rrggbb (DEC-454).

import {
  CATALOG_FONTS,
  CATALOG_FONT_IDS,
  SYSTEM_FONT_ID,
  SYSTEM_FONT_LABEL,
  uploadedFontFamily,
  uploadedFontIdOf,
  type BrandFontCategory,
  type CatalogFont,
} from "./brandFontCatalog.ts";

/** Every free font id (the catalog and the system stack). An uploaded font is `upload:<uuid>`, see brandFontCatalog.ts. */
export const BRAND_FONT_IDS: readonly string[] = CATALOG_FONT_IDS;
export type BrandFontId = string;

/** Derived from the background, never picked (TEN17-F5). Kept because the stored `base` column and the surface
 * derivation use it. */
export const BRAND_BASE_IDS = ["light", "dark"] as const;
export type BrandBaseId = (typeof BRAND_BASE_IDS)[number];

export interface BrandFontOption {
  readonly id: string;
  readonly label: string;
  /** The dropdown group. */
  readonly group: BrandFontCategory;
  /** The display (heading, totals) stack. */
  readonly display: string;
  /** The body and numeric stack. */
  readonly base: string;
}

const INSTRUMENT_BODY = `"Instrument Sans", "Helvetica Neue", Helvetica, Arial, sans-serif`;
const SYSTEM_STACK = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
const SANS_FALLBACK = `"Helvetica Neue", Helvetica, Arial, sans-serif`;
const SERIF_FALLBACK = `Georgia, "Times New Roman", serif`;

/** The four faces TEN17 shipped keep the exact stacks they always had, so a brand saved before TEN17-F5 renders
 * unchanged. */
const LEGACY_STACKS: Readonly<Record<string, { display: string; base: string }>> = {
  "bricolage-grotesque": {
    display: `"Bricolage Grotesque", "Arial Narrow", Arial, sans-serif`,
    base: INSTRUMENT_BODY,
  },
  "instrument-sans": { display: INSTRUMENT_BODY, base: INSTRUMENT_BODY },
  montserrat: { display: `Montserrat, ${SYSTEM_STACK}`, base: INSTRUMENT_BODY },
  bitter: { display: `Bitter, Georgia, "Times New Roman", serif`, base: INSTRUMENT_BODY },
};

function stacksOf(font: CatalogFont): { display: string; base: string } {
  const legacy = LEGACY_STACKS[font.id];
  if (legacy !== undefined) return legacy;
  const own = `"${font.family}", ${font.fallback === "serif" ? SERIF_FALLBACK : SANS_FALLBACK}`;
  return { display: own, base: font.display ? INSTRUMENT_BODY : own };
}

/** The free fonts (the catalog, then the system stack), in dropdown order within each group. */
export const BRAND_FONT_OPTIONS: readonly BrandFontOption[] = [
  ...CATALOG_FONTS.map((font) => ({
    id: font.id,
    label: font.label,
    group: font.category,
    ...stacksOf(font),
  })),
  {
    id: SYSTEM_FONT_ID,
    label: SYSTEM_FONT_LABEL,
    group: "system" as const,
    display: SYSTEM_STACK,
    base: SYSTEM_STACK,
  },
];

/** The stack an uploaded font is set under. The family name is built from the font's row id, never from the file
 * name, so an upload can carry no CSS. */
export function uploadedFontStack(fontRowId: string): string {
  return `"${uploadedFontFamily(fontRowId)}", ${SYSTEM_STACK}`;
}

export interface BrandChoices {
  /** A free font id, or `upload:<uuid>`. */
  readonly font: string;
  readonly accent: string;
  /** The page color. */
  readonly background: string;
  /** The primary text color ("font color"). */
  readonly text: string;
}

/** The token names a brand theme may set, in the order they are written. Anything else is refused by
 * `isSafeBrandTokens`. */
export const BRAND_TOKEN_NAMES = [
  "--font-family-base",
  "--font-family-display",
  "--font-family-numeric",
  "--color-bg",
  "--color-surface",
  "--color-surface-sunken",
  "--color-border",
  "--color-border-strong",
  "--color-text-primary",
  "--color-text-secondary",
  "--color-text-tertiary",
  "--color-text-on-accent",
  "--color-text-on-dark",
  "--color-accent",
  "--color-accent-strong",
  "--color-accent-subtle",
  "--color-accent-subtle-border",
  "--color-focus-ring",
] as const;
export type BrandTokenName = (typeof BRAND_TOKEN_NAMES)[number];
export type BrandTokens = Readonly<Record<BrandTokenName, string>>;

// ---------------------------------------------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------------------------------------------

const HEX = /^#[0-9a-f]{6}$/;

/** Lower-cases and validates a six digit hex color. Returns null for anything else (no named colors, no short
 * form, no alpha): the stored value is always `#rrggbb`. */
export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const lowered = value.trim().toLowerCase();
  return HEX.test(lowered) ? lowered : null;
}

function rgbOf(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}
function hexOf(channels: readonly number[]): string {
  return `#${channels
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}
/** `amount` of `toward` mixed into `from` (0 gives `from`, 1 gives `toward`), per sRGB channel, rounded. */
export function mixHex(from: string, toward: string, amount: number): string {
  const a = rgbOf(from);
  const b = rgbOf(toward);
  return hexOf(a.map((value, i) => value + (b[i]! - value) * amount));
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
export function relativeLuminance(hex: string): number {
  const [r, g, b] = rgbOf(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** WCAG 2.x contrast ratio. */
export function contrastRatio(a: string, b: string): number {
  const x = relativeLuminance(a);
  const y = relativeLuminance(b);
  const [hi, lo] = x >= y ? [x, y] : [y, x];
  return (hi + 0.05) / (lo + 0.05);
}
// ---------------------------------------------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------------------------------------------

/** The page and text colors the Light and Dark bases always used. A brand saved before TEN17-F5 stores only its
 * base; a read fills its background and font color from here, so it renders exactly as it did. */
const LEGACY_COLORS: Record<BrandBaseId, { background: string; text: string }> = {
  light: { background: "#f3f3f1", text: "#17181a" },
  dark: { background: "#131417", text: "#f4f5f7" },
};

export function legacyColorsForBase(base: BrandBaseId): { background: string; text: string } {
  return { ...LEGACY_COLORS[base] };
}

const NEAR_BLACK = "#111111";
const WHITE = "#ffffff";

/** The hand-tuned neutral half of the two palettes TEN17 shipped (the Light and Dark bases). A brand whose
 * background and font color are exactly one of those pairs keeps these values, so a brand saved before TEN17-F5
 * renders exactly as it did; any other pair gets the derivation below. test/ten17-brand-theme.test.ts pins them. */
const LEGACY_NEUTRALS: Record<BrandBaseId, Readonly<Record<string, string>>> = {
  light: {
    "--color-surface": "#ffffff",
    "--color-surface-sunken": "#e7e7e3",
    "--color-border": "#d6d6d0",
    "--color-border-strong": "#73736d",
    "--color-text-secondary": "#3c3e42",
    "--color-text-tertiary": "#585b60",
    "--color-text-on-dark": "#f3f3f1",
  },
  dark: {
    "--color-surface": "#1d1f23",
    "--color-surface-sunken": "#34373d",
    "--color-border": "#2f3238",
    "--color-border-strong": "#8d9199",
    "--color-text-secondary": "#cbced4",
    "--color-text-tertiary": "#a6aab2",
    "--color-text-on-dark": "#f4f5f7",
  },
};

function legacyNeutralsFor(
  background: string,
  text: string,
): Readonly<Record<string, string>> | null {
  for (const base of BRAND_BASE_IDS) {
    const legacy = LEGACY_COLORS[base];
    if (legacy.background === background && legacy.text === text) return LEGACY_NEUTRALS[base];
  }
  return null;
}

/** Dark when white reads better on the background than near black does. */
export function baseOf(background: string): BrandBaseId {
  return contrastRatio(background, WHITE) > contrastRatio(background, NEAR_BLACK)
    ? "dark"
    : "light";
}

/** The option for `id`: a free font, or an uploaded one (`upload:<uuid>`, labelled generically here; the page knows
 * the file's own label). Throws for anything else. */
export function brandFontOption(id: string): BrandFontOption {
  const found = BRAND_FONT_OPTIONS.find((option) => option.id === id);
  if (found !== undefined) return found;
  const rowId = uploadedFontIdOf(id);
  if (rowId !== null) {
    const stack = uploadedFontStack(rowId);
    return { id, label: "Uploaded font", group: "system", display: stack, base: stack };
  }
  throw new Error(`unknown brand font ${id}`);
}

export class BrandChoicesInvalidError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BrandChoicesInvalidError";
  }
}

/** Validates raw input into choices. Throws BrandChoicesInvalidError with a plain sentence. `uploadedFontIds` is the
 * set of `upload:<uuid>` ids the caller's network holds now; any other uploaded id is refused (a removed font, or
 * another network's). */
export function parseBrandChoices(
  input: { font?: unknown; accent?: unknown; background?: unknown; text?: unknown },
  uploadedFontIds: ReadonlySet<string> = new Set(),
): BrandChoices {
  const font =
    typeof input.font === "string" &&
    (BRAND_FONT_IDS.includes(input.font) ||
      (uploadedFontIdOf(input.font) !== null && uploadedFontIds.has(input.font)))
      ? input.font
      : undefined;
  if (font === undefined) throw new BrandChoicesInvalidError("Choose one of the listed fonts.");
  const accent = normalizeHexColor(input.accent);
  if (accent === null) throw new BrandChoicesInvalidError("Choose an accent color.");
  const background = normalizeHexColor(input.background);
  if (background === null) throw new BrandChoicesInvalidError("Choose a background color.");
  const text = normalizeHexColor(input.text);
  if (text === null) throw new BrandChoicesInvalidError("Choose a font color.");
  return { font, accent, background, text };
}

/** The largest step of `amount` (in eighths) of `toward` mixed into `from` that `holds`; `from` itself when none
 * does. Deterministic. */
function mixHolding(
  from: string,
  toward: string,
  amount: number,
  holds: (candidate: string) => boolean,
): string {
  for (let k = 8; k >= 1; k -= 1) {
    const candidate = mixHex(from, toward, (amount * k) / 8);
    if (holds(candidate)) return candidate;
  }
  return from;
}

/** The first of `amounts` (a color mixed from `from` toward `toward`) that `holds`, else `otherwise`. */
function firstMixHolding(
  from: string,
  toward: string,
  amounts: readonly number[],
  holds: (candidate: string) => boolean,
  otherwise: string,
): string {
  for (const amount of amounts) {
    const candidate = mixHex(from, toward, amount);
    if (holds(candidate)) return candidate;
  }
  return otherwise;
}

/** The label color for a button filled with the accent (and with its stronger variant): near black or white as
 * TEN17 always chose; only when that choice cannot reach 4.5:1 on both fills is pure black or white tried, so a
 * mid-tone accent is not refused for a label that a different black or white would make readable. */
function accentLabelColor(accent: string, accentStrong: string): string {
  const first =
    contrastRatio(NEAR_BLACK, accent) >= contrastRatio(WHITE, accent) ? NEAR_BLACK : WHITE;
  for (const candidate of [first, first === WHITE ? NEAR_BLACK : WHITE, "#000000"]) {
    if (
      Math.min(contrastRatio(candidate, accent), contrastRatio(candidate, accentStrong)) >=
      TEXT_CONTRAST_MINIMUM
    ) {
      return candidate;
    }
  }
  return first;
}

/** The full token set for `choices`. Deterministic: the same choices always give the same map. */
export function deriveBrandTokens(choices: BrandChoices): BrandTokens {
  const font = brandFontOption(choices.font);
  const { accent, background: bg, text } = choices;
  const base = baseOf(bg);
  const accentStrong = base === "dark" ? accent : mixHex(accent, "#000000", 0.2);
  // Each derived color is clamped so the font color AND the accent stay readable on it. At the page color itself
  // both hold whenever the picked colors do, so the clamp always finds a step and only a picked color is refused.
  const readable = (against: string) => contrastRatio(text, against) >= TEXT_CONTRAST_MINIMUM;
  const accentReadable = (against: string) =>
    Math.min(contrastRatio(accent, against), contrastRatio(accentStrong, against)) >=
    TEXT_CONTRAST_MINIMUM;
  const holdsBoth = (candidate: string) => readable(candidate) && accentReadable(candidate);
  const surface =
    base === "light"
      ? mixHolding(bg, WHITE, 0.7, holdsBoth)
      : mixHolding(bg, WHITE, 0.06, holdsBoth);
  const sunken =
    base === "light"
      ? mixHolding(bg, text, 0.06, holdsBoth)
      : mixHolding(bg, WHITE, 0.2, holdsBoth);
  const surfaces = [bg, surface, sunken];
  const holdsOnAll = (minimum: number) => (candidate: string) =>
    surfaces.every((surfaceColor) => contrastRatio(candidate, surfaceColor) >= minimum);
  const secondary = firstMixHolding(
    text,
    bg,
    [0.2, 0.15, 0.1, 0.05],
    holdsOnAll(TEXT_CONTRAST_MINIMUM),
    text,
  );
  const tertiary = firstMixHolding(
    text,
    bg,
    [0.35, 0.3, 0.25, 0.2, 0.15, 0.1, 0.05],
    holdsOnAll(TEXT_CONTRAST_MINIMUM),
    text,
  );
  const borderStrong = firstMixHolding(
    bg,
    text,
    [0.55, 0.65, 0.75, 0.85],
    holdsOnAll(OUTLINE_CONTRAST_MINIMUM),
    text,
  );
  const legacy = legacyNeutralsFor(bg, text);
  const onAccent = accentLabelColor(accent, accentStrong);
  const accentSubtle = mixHolding(
    legacy?.["--color-surface"] ?? surface,
    accent,
    base === "dark" ? 0.14 : 0.12,
    (candidate) =>
      readable(candidate) && contrastRatio(accentStrong, candidate) >= TEXT_CONTRAST_MINIMUM,
  );
  const [r, g, b] = rgbOf(accent);
  const tokens: Record<string, string> = {
    "--font-family-base": font.base,
    "--font-family-display": font.display,
    "--font-family-numeric": "var(--font-family-base)",
    "--color-bg": bg,
    "--color-surface": surface,
    "--color-surface-sunken": sunken,
    "--color-border": mixHex(bg, text, 0.15),
    "--color-border-strong": borderStrong,
    "--color-text-primary": text,
    "--color-text-secondary": secondary,
    "--color-text-tertiary": tertiary,
    "--color-text-on-dark": base === "dark" ? text : bg,
    "--color-text-on-accent": onAccent,
    "--color-accent": accent,
    "--color-accent-strong": accentStrong,
    "--color-accent-subtle": accentSubtle,
    "--color-accent-subtle-border": mixHex(bg, accent, base === "dark" ? 0.45 : 0.4),
    "--color-focus-ring": `rgba(${r}, ${g}, ${b}, 0.6)`,
  };
  if (legacy !== null) Object.assign(tokens, legacy);
  const ordered = {} as Record<BrandTokenName, string>;
  for (const name of BRAND_TOKEN_NAMES) ordered[name] = tokens[name]!;
  return ordered;
}

// ---------------------------------------------------------------------------------------------------------------
// The rules a derived theme must pass (the shipped themes pass the same: test/ux83-snipsip-themes.test.ts)
// ---------------------------------------------------------------------------------------------------------------

/** WCAG AA text ratio. Used only to DERIVE secondary text and the label on the accent; it never refuses a pick
 * (DEC-454: the franchisor may choose any valid colors). */
export const TEXT_CONTRAST_MINIMUM = 4.5;
/** WCAG 2.2 1.4.11 non-text contrast, used only to derive the control outline color. */
export const OUTLINE_CONTRAST_MINIMUM = 3;

// ---------------------------------------------------------------------------------------------------------------
// Safe application of a token set in a page
// ---------------------------------------------------------------------------------------------------------------

const COLOR_VALUE = /^(#[0-9a-f]{6}|rgba\(\d{1,3}, \d{1,3}, \d{1,3}, (0|1|0?\.\d{1,2})\))$/;
const FONT_STACKS: ReadonlySet<string> = new Set(
  BRAND_FONT_OPTIONS.flatMap((option) => [option.display, option.base]),
);
const UPLOADED_STACK = new RegExp(
  `^"brand-upload-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}", ${SYSTEM_STACK.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
);

/** True only for a map holding EXACTLY the allowed token names, each a color of the allowed shape, a font stack
 * from the fixed list (or an uploaded font's fixed shape), or the one numeric alias. Anything else (an extra name, a `;` or `}` in a value, a layout
 * token) is refused, so the page can write the map into a style rule without it ever carrying markup or layout. */
export function isSafeBrandTokens(value: unknown): value is BrandTokens {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== BRAND_TOKEN_NAMES.length) return false;
  for (const name of BRAND_TOKEN_NAMES) {
    const v = record[name];
    if (typeof v !== "string") return false;
    if (name === "--font-family-numeric") {
      if (v !== "var(--font-family-base)") return false;
    } else if (name === "--font-family-base" || name === "--font-family-display") {
      if (!FONT_STACKS.has(v) && !UPLOADED_STACK.test(v)) return false;
    } else if (!COLOR_VALUE.test(v)) {
      return false;
    }
  }
  return true;
}

/** The theme id a network's brand is stored under in `users.default_theme_id`. */
export const BRAND_THEME_ID_PREFIX = "brand:";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function brandThemeId(networkId: string): string {
  return `${BRAND_THEME_ID_PREFIX}${networkId}`;
}
/** The network id inside a `brand:<uuid>` theme id, or null when the id is not of that exact shape. */
export function networkIdFromBrandThemeId(themeId: string): string | null {
  if (!themeId.startsWith(BRAND_THEME_ID_PREFIX)) return null;
  const id = themeId.slice(BRAND_THEME_ID_PREFIX.length);
  return UUID.test(id) ? id : null;
}

/** The CSS rule text for a brand theme: one block under `[data-theme="brand:<id>"]` holding only the allowed
 * custom properties. Throws on an id or token map that is not safe. */
export function brandThemeCss(themeId: string, tokens: BrandTokens): string {
  if (networkIdFromBrandThemeId(themeId) === null) throw new Error("not a brand theme id");
  if (!isSafeBrandTokens(tokens)) throw new Error("brand tokens are not safe to write");
  const lines = BRAND_TOKEN_NAMES.map((name) => `  ${name}: ${tokens[name]};`);
  return `[data-theme="${themeId}"] {\n${lines.join("\n")}\n}\n`;
}

/** The custom property map for a bare preview element (no selector). */
export function brandPreviewDeclarations(
  tokens: BrandTokens,
): ReadonlyArray<readonly [BrandTokenName, string]> {
  if (!isSafeBrandTokens(tokens)) throw new Error("brand tokens are not safe to write");
  return BRAND_TOKEN_NAMES.map((name) => [name, tokens[name]] as const);
}
